import { FC, useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router';

import { locationChange } from '@rikstv/play-common/src/forces/ui/ui.slice';
import { authService } from '@rikstv/play-common/src/utils/auth/AuthService';

import { useIsAuthenticated } from '../hooks/useIsAuthenticated';

// Export hook through a React component so it can easily and safely be passed
// as prop down to CommonRouter
export const OnRouteChange: FC = () => {
  useRouteChange();
  return null;
};

let lastLocation: string | undefined;

const useRouteChange = () => {
  const dispatch = useDispatch();
  const isLoggedIn = useIsAuthenticated();
  const scrollAttempts = useRef<number>(0);
  const scrollTimers = useRef<number[]>([]);

  const logLocationChange = useCallback(
    (pathname: string, isLoggedIn: boolean) => {
      // Avoid multiple events for the same pathname
      const newLocation = [pathname, isLoggedIn].join();
      if (lastLocation !== newLocation) {
        lastLocation = newLocation;
        dispatch(locationChange({ pathname, isLoggedIn }));
      }
    },
    [dispatch]
  );

  const { pathname, state } = useLocation();

  const s = state as { scrollRestore?: { swimlaneId: string; elementIndex: number }; verticalScroll?: number };

  const restoreScrollPosition = useCallback((targetScroll: number) => {
    // Clear any existing timers
    scrollTimers.current.forEach(timer => clearTimeout(timer));
    scrollTimers.current = [];
    scrollAttempts.current = 0;

    // Function to check if we've scrolled to the right position
    const scrollToPosition = () => {
      // First scroll to the target position
      window.scrollTo(0, targetScroll);

      // Wait a moment before checking if we're in the right position
      const checkTimer = window.setTimeout(() => {
        if (Math.abs(window.scrollY - targetScroll) > 10 && scrollAttempts.current < 3) {
          scrollAttempts.current++;
          let delay = 1000;
          switch (scrollAttempts.current) {
            case 1:
              delay = 75;
              break;
            case 2:
              delay = 175;
              break;
            case 3:
              delay = 250;
              break;
          }

          const timer = window.setTimeout(scrollToPosition, delay);
          scrollTimers.current.push(timer);
        }
      }, 175); // 175ms delay before checking position

      scrollTimers.current.push(checkTimer);
    };

    // Initial scroll attempt after layout is likely stable
    const initialTimer = window.setTimeout(scrollToPosition, 25);
    scrollTimers.current.push(initialTimer);
  }, []);

  useEffect(() => {
    // Clean up all timers on unmount
    return () => {
      scrollTimers.current.forEach(timer => clearTimeout(timer));
    };
  }, []);

  useEffect(() => {
    // Track screen view on location changed
    authService.shouldRenewTokensOnRouteChange().then(shouldRenew => {
      if (shouldRenew) {
        authService.renewTokens({ redirectUrl: pathname });
      } else {
        logLocationChange(pathname, isLoggedIn);

        const restoreScroll = s?.verticalScroll;
        if (restoreScroll !== undefined) {
          restoreScrollPosition(restoreScroll);
        }
      }
    });

    // Cleanup function
    return () => {
      scrollTimers.current.forEach(timer => clearTimeout(timer));
      scrollTimers.current = [];
    };
  }, [pathname, s, isLoggedIn, logLocationChange, restoreScrollPosition]);
};
